import { useState, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Country, EmployeeNameSearchInfo } from '../../types'
import { EmployeeWithRole } from '../../types'
import { PerInfoBox } from '../../components/custom_components/InfoBox'
import { InfoBoxTile } from '../../components/custom_components/InfoBox/InfoBoxTileTemplate/InfoBoxTile'
import { PerInfoBoxDivider } from '../../components/custom_components/InfoBox/InfoBoxDivider'
import { PerInfoBoxActionButtonElement } from '../../components/custom_components/InfoBox/InfoBoxActionButtonElement'
import { PerInfoBoxTagElement } from '../../components/custom_components/InfoBox/InfoBoxTagElement'
import { PerInfoBoxNumberElement } from '../../components/custom_components/InfoBox/InfoBoxNumberElement'
import { GlobalAdminAddRoleModal } from './GlobalAdminAddRoleModal'
import { addHrRoleToEmployee, addDownloaderRoleToEmployee } from '../../graphql/roles/hooks/useMutateRoles'
import { convertEmployeeFromSearch, convertEmployeeToSearch } from './utils'
import { showNotification } from '../../components/custom_components/Notification/notification'

interface GlobalAdminInfoBoxProps {
  infoboxData: EmployeeWithRole[]
  availableCountries: Country[]
}

export const GlobalAdminInfoBox: FC<GlobalAdminInfoBoxProps> = ({ infoboxData, availableCountries }) => {
  const { t } = useTranslation()
  const countAdminRoles = infoboxData.reduce((countAdminRoles, currentValue) => {
    const count = currentValue.hrRoleOfUser.filter((roleOfUser) => roleOfUser.role.name.includes('ADMIN')).length
    return countAdminRoles + count
  }, 0)
  const readOnlyRoles = infoboxData.reduce((readOnlyRoles, currentValue) => {
    const count = currentValue.hrRoleOfUser.filter((roleOfUser) => roleOfUser.role.name.includes('READONLY')).length
    return readOnlyRoles + count
  }, 0)

  const [open, setOpen] = useState(false)
  const [countryOptions, setCountryOptions] = useState(availableCountries)
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(undefined)
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithRole | undefined>(undefined)
  const [roleType, setRoleType] = useState<'admin' | 'readonly' | 'downloader' | undefined>(undefined)
  const [hrVisibilityExcluded, setHrVisibilityExcluded] = useState<boolean>(false)
  const [modalError, setModalError] = useState<string | undefined>(undefined)

  const addRoleToEmployee = async () => {
    // Add HR role or Downloader role
    if (selectedEmployee && selectedCountry && roleType && ['admin', 'readonly'].includes(roleType)) {
      const { errors } = await addHrRoleToEmployee(
        selectedEmployee.id,
        selectedCountry,
        roleType === 'admin' ? 'admin' : 'readonly',
        hrVisibilityExcluded,
      )
      if (!!errors && !!errors.length) {
        setModalError(t('global.notifications.error.description'))
      } else {
        showNotification({
          type: 'success',
          message: t('globalAdminView.infoBox.notifications.hr.success.title'),
          description: t('globalAdminView.infoBox.notifications.hr.success.description', {
            role: roleType,
            employeeFirstName: selectedEmployee.firstName,
            country: selectedCountry,
          }),
        })
      }
    } else if (selectedEmployee && roleType && ['downloader'].includes(roleType)) {
      const { errors } = await addDownloaderRoleToEmployee(selectedEmployee.id)
      if (!!errors && !!errors.length) {
        setModalError(t('global.notifications.error.description'))
      } else {
        showNotification({
          type: 'success',
          message: t('globalAdminView.infoBox.notifications.downloader.success.title'),
          description: t('globalAdminView.infoBox.notifications.downloader.success.description', {
            employeeFirstName: selectedEmployee.firstName,
          }),
        })
      }
    }
    onCancel()
  }

  const onCancel = () => {
    setOpen(false)
    setSelectedEmployee(undefined)
    setSelectedCountry(undefined)
    setRoleType(undefined)
    setHrVisibilityExcluded(false)
    setModalError(undefined)
  }

  const onSelectEmployee = (innerSelectedEmployee: EmployeeNameSearchInfo) => {
    const existingEmployee = infoboxData.find((data) => `${data.id}` === `${innerSelectedEmployee.id}`)
    if (existingEmployee) {
      const roleNames = existingEmployee.hrRoleOfUser.map((roleOfUser) => roleOfUser.role.name.split('_')[2])
      const availableCountriesForSelectedEmployee = availableCountries.filter((country) => !roleNames.includes(country))
      setSelectedEmployee(existingEmployee)
      setCountryOptions(availableCountriesForSelectedEmployee)
    } else {
      setSelectedEmployee(convertEmployeeFromSearch(innerSelectedEmployee))
      setCountryOptions(availableCountries)
    }
  }

  const onSelectType = (type: 'admin' | 'readonly' | 'downloader') => {
    if (['admin', 'readonly'].includes(type)) {
      setRoleType(type)
    }
    if (['downloader'].includes(type)) {
      setRoleType(type)
      setHrVisibilityExcluded(false)
      setSelectedCountry(undefined)
    }
  }

  return (
    <>
      <GlobalAdminAddRoleModal
        open={open}
        onCancel={onCancel}
        saveChanges={addRoleToEmployee}
        countryOptions={countryOptions}
        roleType={roleType}
        hrVisibilityExcluded={hrVisibilityExcluded}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        selectedEmployee={selectedEmployee ? convertEmployeeToSearch(selectedEmployee) : undefined}
        onSelectEmployee={(employee) => onSelectEmployee(employee)}
        setRoleType={(type) => onSelectType(type)}
        setHrVisibilityExcluded={setHrVisibilityExcluded}
        infoboxData={infoboxData}
        error={modalError}
      />

      <PerInfoBox>
        <InfoBoxTile heading={t('globalAdminView.infoBox.employees.heading')} contentFlex="fixed">
          <PerInfoBoxNumberElement number={infoboxData.length} />
        </InfoBoxTile>

        <PerInfoBoxDivider />

        <InfoBoxTile heading={t('globalAdminView.infoBox.assignedRoles.heading')}>
          <PerInfoBoxTagElement name="Admin" color="cyan" number={countAdminRoles} />
          <PerInfoBoxTagElement name="Read only" color="lime" number={readOnlyRoles} />
        </InfoBoxTile>

        <PerInfoBoxDivider />

        <InfoBoxTile heading={t('globalAdminView.infoBox.add.heading')} headingAlign="center" contentFlex="fixed" contentAlign="center">
          <PerInfoBoxActionButtonElement icon="add_stroke" color="success" tooltip={t('tooltip.add')} onClick={() => setOpen(true)} />
        </InfoBoxTile>
      </PerInfoBox>
    </>
  )
}
